import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable, Column } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Receipt, Eye, RefreshCw, Plus, Filter, ChevronRight } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { DriverCodSettlement } from '@/types/financial.types';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { GenerateCodSettlementModal } from '@/components/features/financials/GenerateCodSettlementModal';
import { DriverSettlementFilterSidebar, DriverSettlementFilterValues } from '@/components/features/financials/DriverSettlementFilterSidebar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useI18n } from '@/contexts/I18nContext';

const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground border-border',
  confirmed: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  paid: 'bg-green-500/10 text-green-600 border-green-500/20',
  reversed: 'bg-red-500/10 text-red-600 border-red-500/20',
};

const initialFilters: DriverSettlementFilterValues = {
  status: '',
  date_from: '',
  date_to: '',
};

export default function DriverSettlementsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { get, loading } = useApi();
  const [settlements, setSettlements] = useState<DriverCodSettlement[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [filters, setFilters] = useState<DriverSettlementFilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const STATUS_LABEL: Record<string, string> = {
    draft: t.financials.status_draft,
    confirmed: t.financials.status_confirmed,
    paid: t.financials.status_paid,
    reversed: t.financials.status_reversed,
  };

  useEffect(() => {
    fetchSettlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettlements = async () => {
    setHasFetched(false);
    try {
      const result: any = await get('/api/v1/driver-cod-settlements');
      const data = result?.success !== undefined ? result?.data : result;
      const list = Array.isArray(data) ? data : data?.list ?? data?.data ?? [];
      setSettlements(list);
    } catch {
      toast.error('فشل في تحميل التسويات');
    } finally {
      setHasFetched(true);
    }
  };

  const handleFilterChange = (key: keyof DriverSettlementFilterValues, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  const filtered = settlements.filter((s) => {
    if (filters.status && s.status !== filters.status) return false;
    if (filters.date_from) {
      const from = new Date(filters.date_from);
      const created = new Date(s.created_at);
      if (created < from) return false;
    }
    if (filters.date_to) {
      const to = new Date(filters.date_to);
      to.setHours(23, 59, 59, 999);
      const created = new Date(s.created_at);
      if (created > to) return false;
    }
    return true;
  });

  const columns: Column<DriverCodSettlement>[] = [
    {
      header: 'السائق',
      accessor: (row) => (
        <div>
          <div className="font-medium">{row.driver_name}</div>
          {row.driver_phone && (
            <div className="text-xs text-muted-foreground" dir="ltr">{row.driver_phone}</div>
          )}
        </div>
      ),
      className: 'min-w-[160px]',
    },
    {
      header: 'قيمة الشحنات',
      accessor: (row) => (
        <span className="font-medium text-green-600">{formatCurrency(row.shipment_value)}</span>
      ),
      className: 'min-w-[120px]',
    },
    {
      header: 'رسوم التوصيل',
      accessor: (row) => (
        <span className="font-medium text-blue-600">{formatCurrency(row.delivery_fee)}</span>
      ),
      className: 'min-w-[120px]',
    },
    {
      header: 'الإجمالي',
      accessor: (row) => (
        <span className="font-bold text-primary">{formatCurrency(row.total_amount)}</span>
      ),
      className: 'min-w-[120px]',
    },
    {
      header: 'الحالة',
      accessor: (row) => (
        <Badge className={cn('border text-xs', STATUS_COLOR[row.status])}>
          {STATUS_LABEL[row.status] ?? row.status}
        </Badge>
      ),
      className: 'min-w-[100px]',
    },
    {
      header: 'تاريخ الإنشاء',
      accessor: (row) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.created_at)}</span>
      ),
      className: 'min-w-[120px]',
    },
  ];

  const FilterSidebarContent = (
    <DriverSettlementFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  const currentTab =
    location.pathname.startsWith('/dashboard/driver-pay-settlements') ? 'pay' : 'cod';

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">{t.financials.cod_settlements}</h1>
              <p className="text-sm text-muted-foreground">تسويات التحصيل النقدي للسائقين</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">{FilterSidebarContent}</div>
              </SheetContent>
            </Sheet>
            <Button variant="outline" onClick={fetchSettlements} disabled={loading} className="gap-2">
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
              تحديث
            </Button>
            <Button onClick={() => setShowGenerateModal(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              {t.financials.generate_cod_settlement}
            </Button>
          </div>
        </div>

        <Tabs
          value={currentTab}
          onValueChange={(v) => {
            if (v === 'pay') navigate('/dashboard/driver-pay-settlements');
          }}
          className="mt-4"
        >
          <TabsList>
            <TabsTrigger value="cod">{t.financials.cod_settlements}</TabsTrigger>
            <TabsTrigger value="pay">{t.financials.pay_settlements}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex gap-4 ">
        <div className="hidden lg:block">
          {FilterSidebarContent}
        </div>
        <div className="flex-1 min-w-0">
          {!hasFetched ? (
            <LoadingSkeleton type="table" rows={8} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<Receipt className="h-12 w-12" />}
              title={t.financials.no_unsettled_cod}
              description="لم يتم العثور على تسويات للسائقين"
            />
          ) : (
            <DataTable
              data={filtered}
              columns={columns}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              actions={(row) => (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => navigate(`/dashboard/driver-cod-settlements/${row.id}`)}
                  title="عرض التفاصيل"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              )}
              emptyMessage="لا توجد تسويات"
            />
          )}
        </div>
      </div>

      <GenerateCodSettlementModal
        open={showGenerateModal}
        onOpenChange={setShowGenerateModal}
        onSuccess={fetchSettlements}
      />
    </PageContainer>
  );
}
